import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HelpOutline as HelpIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { alpha, Box, Skeleton, Typography } from "@mui/material";
import { type FC, memo } from "react";
import type { OverallStatusType } from "../../types";
import { UPTIME_COLORS } from "../../utils/uptime-colors";

interface OverallStatusBannerProps {
  status?: OverallStatusType;
  isLoading?: boolean;
}

const STATUS_CONFIG: Record<
  OverallStatusType,
  {
    label: string;
    backgroundColor: string;
    borderColor: string;
    textColor: string;
    iconColor: string;
    icon: React.ReactNode;
  }
> = {
  operational: {
    label: "All Systems Operational",
    backgroundColor: alpha(UPTIME_COLORS.operational, 0.1),
    borderColor: alpha(UPTIME_COLORS.operational, 0.3),
    textColor: "success.dark",
    iconColor: UPTIME_COLORS.operational,
    icon: <CheckCircleIcon sx={{ fontSize: 28 }} />,
  },
  degraded: {
    label: "Some Systems Experiencing Issues",
    backgroundColor: alpha(UPTIME_COLORS.degraded, 0.1),
    borderColor: alpha(UPTIME_COLORS.degraded, 0.3),
    textColor: "warning.dark",
    iconColor: UPTIME_COLORS.degraded,
    icon: <WarningIcon sx={{ fontSize: 28 }} />,
  },
  outage: {
    label: "Major System Outage",
    backgroundColor: alpha(UPTIME_COLORS.outage, 0.1),
    borderColor: alpha(UPTIME_COLORS.outage, 0.3),
    textColor: "error.dark",
    iconColor: UPTIME_COLORS.outage,
    icon: <ErrorIcon sx={{ fontSize: 28 }} />,
  },
  unknown: {
    label: "Status Unknown",
    backgroundColor: alpha(UPTIME_COLORS.unknown, 0.1),
    borderColor: alpha(UPTIME_COLORS.unknown, 0.3),
    textColor: "grey.800",
    iconColor: UPTIME_COLORS.unknown,
    icon: <HelpIcon sx={{ fontSize: 28 }} />,
  },
};

export const OverallStatusBanner: FC<OverallStatusBannerProps> = memo(({ status, isLoading }) => {
  if (isLoading) {
    return (
      <Skeleton variant="rectangular" sx={{ borderRadius: 2, height: "100%", minHeight: 56 }} />
    );
  }

  const config = STATUS_CONFIG[status ?? "unknown"];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 3,
        py: 2,
        borderRadius: 2,
        backgroundColor: config.backgroundColor,
        border: "1px solid",
        borderColor: config.borderColor,
        color: config.textColor,
        height: "100%",
        minHeight: 56,
      }}
    >
      <Box sx={{ color: config.iconColor }}>{config.icon}</Box>
      <Typography variant="h6" fontWeight={600}>
        {config.label}
      </Typography>
    </Box>
  );
});

OverallStatusBanner.displayName = "OverallStatusBanner";
